import { gql, useQuery } from '@apollo/client'
import {
  createRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { matchPath } from 'react-router'

import { config } from '@/config'
import { User, useRouter } from '@/core'
import { getRegisterNewMembership } from '@/core/native/storage'

export const needSettingPersonalUserSnackbarRef = createRef<{
  triggerShowMessage: (isRegisterNewMembership?: boolean) => void
}>()

export const useNeedSettingPersonalUserSnackbar = (user: User | null) => {
  // store the artistId when it is shown
  const showedArtistRef = useRef<string[]>([])
  const showedSnackbarOnMyProfile = useRef<boolean>(false)
  const { location, routerPages } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const [isRegisterNewMembership, setIsRegisterNewMembership] =
    useState<boolean>(false)
  const isMyProfilePage = useMemo(
    () => location.pathname === routerPages.MyProfile,
    [location.pathname]
  )

  // get artistId from url
  const artistId = useMemo<string | undefined>(() => {
    if (config.applicationType === 'standalone') {
      return config.artistId
    }
    // router.query.artistId is undefined because this component is placed outside the Route component of react-router
    const match = matchPath<{ artistId?: string }>(location.pathname, {
      path: '/artists/:artistId',
      strict: false,
    })
    return match?.params?.artistId
  }, [location.pathname])

  const skipQuery = useMemo(() => {
    if (!user?.uid) {
      return true
    }
    if (isMyProfilePage) {
      return false
    }
    return !artistId
  }, [artistId, isMyProfilePage, user?.uid])

  const { data } = useQuery(QUERY, {
    skip: skipQuery,
    variables: { id: artistId, skipArtist: !artistId },
    onCompleted({ artist, user }) {
      if (
        user?.isMembershipPersonalInformationRequired &&
        isMyProfilePage &&
        !showedSnackbarOnMyProfile.current
      ) {
        showedSnackbarOnMyProfile.current = true
        setOpen(true)
        return
      }
      if (!artist) {
        return
      }
      const artistExisted = !!showedArtistRef.current.find(
        artistId => artistId === artist.id
      )
      if (!artist?.patron?.isPersonalInformationRequired) {
        showedArtistRef.current = showedArtistRef.current.filter(
          artistId => artistId !== artist.id
        )
      }
      if (artist?.patron?.isPersonalInformationRequired && !artistExisted) {
        setOpen(true)
        showedArtistRef.current.push(artist.id)
        return
      }
    },
  })

  useEffect(() => {
    ;(async () => {
      setIsRegisterNewMembership(await getRegisterNewMembership())
    })()
  }, [open])

  useImperativeHandle(needSettingPersonalUserSnackbarRef, () => ({
    async triggerShowMessage(isRegisterNewMembership) {
      if (isRegisterNewMembership !== undefined) {
        setIsRegisterNewMembership(isRegisterNewMembership)
      }
      setOpen(true)
    },
  }))

  // お客様情報未入力 → 登録完了した場合はスナックバーを非表示にする
  useEffect(() => {
    if (!data?.user?.isMembershipPersonalInformationRequired) {
      setOpen(false)
    }
  }, [data?.user?.isMembershipPersonalInformationRequired])

  return {
    open,
    type: !isRegisterNewMembership ? 'resume' : 'input',
    onClose() {
      setOpen(false)
    },
  }
}

const QUERY = gql`
  query NeedSettingPersonalUserSnackbar($id: ID, $skipArtist: Boolean!) {
    user {
      id
      isMembershipPersonalInformationRequired
    }
    artist(id: $id) @skip(if: $skipArtist) {
      id
      patron {
        id
        isPersonalInformationRequired
      }
    }
  }
`
